import { Button, useToast } from "@astryxdesign/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Trip } from "../types/api";

const TERMINAL_TASK_STATUSES = new Set(["COMPLETED", "FAILED", "CANCELLED"]);

export function CompleteTripButton({ trip }: { trip: Trip }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const complete = useMutation({
    mutationFn: () => api.patch(`/api/v2/trips/${trip.id}/complete`),
    onSuccess: () => {
      toast({ body: "Trip finished" });
      void queryClient.invalidateQueries({ queryKey: ["trip", trip.id] });
      void queryClient.invalidateQueries({ queryKey: ["operator-trips"] });
    },
    onError: () => {
      toast({
        body: "Couldn't finish trip. Refresh to check for unfinished work, then try again.",
        type: "error",
      });
      void queryClient.invalidateQueries({ queryKey: ["trip", trip.id] });
    },
  });

  const tasks =
    trip.tasks ??
    (trip.waypoints?.every((waypoint) => waypoint.tasks !== undefined)
      ? trip.waypoints.flatMap((waypoint) => waypoint.tasks ?? [])
      : undefined);
  const canComplete =
    trip.status === "ACTIVE" &&
    tasks !== undefined &&
    tasks.every((task) => TERMINAL_TASK_STATUSES.has(task.status));
  if (!canComplete) return null;

  return (
    <Button
      variant="primary"
      label="Finish trip"
      isLoading={complete.isPending}
      isDisabled={complete.isPending}
      onClick={() => complete.mutate()}
    />
  );
}
